import React, { useState } from "react";
import { User, Phone, Mail, CreditCard, X } from "lucide-react";
import { MdOutlineMapsHomeWork } from "react-icons/md";
import { GrGallery } from "react-icons/gr";
import axios from "axios";
import UploadImg from "../Cloudinary/UploadImg";

const EnquiryForConsultant = () => {
  const [formData, setFormData] = useState({
    consultantName: "",
    phone: "",
    email: "",
    companyName: "",
    companyRegistrationProof: null,
    country: "",
    message: "",
    aadharCard: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);

  const closeSuccessMessage = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let companyProofUrl = "";
      if (formData.companyRegistrationProof instanceof File) {
        companyProofUrl = await UploadImg(formData.companyRegistrationProof);
      }

      const data = {
        consultantName: formData.consultantName,
        phone: formData.phone,
        email: formData.email,
        companyName: formData.companyName,
        companyRegistrationProof: companyProofUrl,
        country: formData.country,
        message: formData.message,
        aadharCard: formData.aadharCard,
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/consultant-enquiry/create`,
        data,
        { headers: { "Content-Type": "application/json" } }
      );

      setSuccess("Consultant enquiry submitted successfully!");
      setShowModal(true);

      setFormData({
        consultantName: "",
        phone: "",
        email: "",
        companyName: "",
        companyRegistrationProof: null,
        country: "",
        message: "",
        aadharCard: "",
      });
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        err.response?.data?.message || "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 flex justify-center items-start min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6 xl:px-8">
      <div className="w-full grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Left Banner */}
        <div className="hidden lg:flex col-span-1 relative rounded-2xl shadow-xl overflow-hidden min-h-[600px] xl:min-h-[700px] transform hover:scale-[1.02] transition-transform duration-300">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0070a8] to-[#023955]"></div>
          <div className="absolute inset-0 bg-black/20"></div>

          <div className="relative z-10 flex flex-col items-center justify-center text-center text-white p-6 xl:p-10 space-y-4 xl:space-y-6">
            <h2 className="text-2xl xl:text-4xl font-extrabold tracking-tight drop-shadow-lg leading-tight">
              Consultant Enquiry
            </h2>
            <div className="w-20 xl:w-24 h-1 bg-white/70 rounded-full mx-auto"></div>

            <div className="space-y-4 xl:space-y-5 text-left w-full max-w-xs">
              {[
                {
                  title: "Online/Offline Meeting",
                  desc: "Provide flexible meeting options for clients through both online and offline channels.",
                },
                {
                  title: "Consultant Form",
                  desc: "Record and maintain all necessary details and legal documents for smooth consultancy operations.",
                },
                {
                  title: "Documentation Submission",
                  desc: "Ensure accurate and timely submission of required documents to support seamless processes.",
                },
                {
                  title: "Consultant Fee Payment",
                  desc: "Manage and process payments efficiently with a clear and transparent fee structure.",
                },
                {
                  title: "Agreement Signing",
                  desc: "Establish clear consultancy terms and manage agreements with ease and transparency.",
                },
                {
                  title: "Consultant Certificate",
                  desc: "Issue valid registration certificates to build trust and ensure authenticity.",
                },
                {
                  title: "Offline Training",
                  desc: "Conduct in-person training sessions to provide hands-on learning and guidance.",
                },
              ].map((item, index) => (
                <div key={index} className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 xl:p-2.5 rounded-lg shadow-inner flex-shrink-0">
                      <GrGallery
                        size={16}
                        className="xl:w-5 xl:h-5 text-white"
                      />
                    </div>
                    <span className="text-sm xl:text-base font-semibold">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-xs xl:text-sm text-gray-200 leading-snug">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="lg:col-span-2 bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden">
          <form
            onSubmit={handleSubmit}
            className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6"
          >
            {error && (
              <div className="p-3 bg-red-100 text-red-700 text-sm sm:text-base text-center rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                Consultant Application
              </h2>
              <p className="text-gray-500 text-sm sm:text-base">
                Fill out the form below and we'll get back to you shortly.
              </p>
            </div>

            {/* Consultant Name + Company Name */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Full Name */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Full Name *
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <User
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                  />
                  <input
                    type="text"
                    name="consultantName"
                    value={formData.consultantName}
                    onChange={handleChange}
                    placeholder="Enter Your Name"
                    required
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Company Name (Optional)
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <MdOutlineMapsHomeWork
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                  />
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Company Name"
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Email *
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <Mail
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address"
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Phone *
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <Phone
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Mobile Number"
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>
            </div>

            {/* Country + Aadhar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Country (Optional)
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <svg
                    className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Enter Country"
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Aadhar Card Number (Optional)
                </label>
                <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                  <CreditCard
                    size={16}
                    className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                  />
                  <input
                    type="text"
                    name="aadharCard"
                    value={formData.aadharCard}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length > 12) value = value.slice(0, 12);
                      value = value.replace(/(\d{4})(?=\d)/g, "$1 ");
                      setFormData({ ...formData, aadharCard: value });
                    }}
                    placeholder="1234 5678 9012"
                    className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700"
                  />
                </div>
              </div>
            </div>

            {/* Company Registration Proof */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Company Registration Proof (Optional)
              </label>
              <div className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#0070a8] transition-colors duration-200">
                <GrGallery
                  size={16}
                  className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0"
                />
                <input
                  type="file"
                  name="companyRegistrationProof"
                  accept="image/*"
                  onChange={handleChange}
                  className="bg-transparent outline-none w-full text-sm sm:text-base text-gray-700 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Message (Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your message"
                rows={4}
                className="rounded-xl bg-gray-50 px-3 sm:px-4 py-3 border border-gray-200 outline-none w-full text-sm sm:text-base resize-none focus:border-[#0070a8] transition-colors duration-200 text-gray-700"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={loading}
                className={`bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white font-medium px-4 sm:px-6 py-2 sm:py-3 rounded-xl text-sm sm:text-base transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Submitting..." : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 max-w-md w-full text-center relative animate-bounce-in">
            {/* Close Icon */}
            <button
              onClick={closeSuccessMessage}
              className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-500 hover:text-gray-800 transition-colors duration-200"
            >
              <X size={18} className="sm:w-[22px] sm:h-[22px]" />
            </button>

            <div className="text-4xl sm:text-6xl mb-3 sm:mb-4 animate-bounce">
              🎉
            </div>
            <div className="w-12 sm:w-16 bg-gradient-to-r from-green-400 to-emerald-500 mx-auto mb-3 sm:mb-4 rounded-full h-1"></div>

            <h2 className="text-green-700 font-bold text-lg sm:text-xl mb-2">
              Form Submitted Successfully!
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4">
              Thank you for reaching out. Our team will contact you shortly.
            </p>

            <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-green-200">
              <p className="text-gray-600 text-xs sm:text-sm italic">
                You can also connect with us via{" "}
                <a
                  href="https://wa.me/919337233933"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-green-600 hover:text-green-700 underline transition-colors"
                >
                  WhatsApp (9337233933) Chat
                </a>{" "}
                or{" "}
                <a
                  href="tel:+919337233933"
                  className="font-semibold text-blue-600 hover:text-blue-700 underline transition-colors"
                >
                  Call (9337233933)
                </a>{" "}
                for quick assistance.
              </p>
            </div>

            <button
              onClick={closeSuccessMessage}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-[#0070a8] to-[#045b87] text-white rounded-lg hover:from-[#1e8cbf] hover:to-[#1674a3] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm sm:text-base"
            >
              Submit Another Inquiry
            </button>
          </div>
        </div>
      )}

      <style jsx="true">{`
        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EnquiryForConsultant;
